import { useMemo, useState } from 'react';

import type { ParsedRedlineItem } from '../../../helpers/redlineCodeParser';
import { parseJsonlToRedlineList } from '../../../helpers/redlineCodeParser';

export function useStableParsedRedlineItems(code: string): ParsedRedlineItem[] {
  const parsedItems = useMemo(() => parseJsonlToRedlineList(code), [code]);
  const [stableParsedState, setStableParsedState] = useState(() =>
    createStableParsedState([], parsedItems)
  );

  if (stableParsedState.parsedItems !== parsedItems) {
    // Derive during render so streaming chunks never commit a stale redline list.
    const nextStableParsedState = createStableParsedState(
      stableParsedState.stableParsedItems,
      parsedItems
    );
    setStableParsedState(nextStableParsedState);

    return nextStableParsedState.stableParsedItems;
  }

  return stableParsedState.stableParsedItems;
}

interface StableParsedState {
  parsedItems: ParsedRedlineItem[];
  stableParsedItems: ParsedRedlineItem[];
}

function createStableParsedState(
  previousItems: ParsedRedlineItem[],
  parsedItems: ParsedRedlineItem[]
): StableParsedState {
  return {
    parsedItems,
    stableParsedItems: parsedItems.length
      ? mergeParsedItems(previousItems, parsedItems)
      : previousItems,
  };
}

function getStableRedlineItemKey(item: ParsedRedlineItem) {
  switch (item.type) {
    case 'redline-edit-error':
      return `error:${item.groupId}`;
    case 'redline-edit':
      return `edit:${item.actions.map(action => action.groupId).join('|')}`;
    case 'redline-insert':
      return `insert:${item.groupId}`;
    case 'redline-remove':
      return `remove:${item.groupId}`;
    case 'redline-note':
      return `note:${item.groupId}`;
  }
}

function mergeParsedItems(
  previousItems: ParsedRedlineItem[],
  parsedItems: ParsedRedlineItem[]
): ParsedRedlineItem[] {
  const itemsByKey = new Map<string, ParsedRedlineItem>();
  for (const item of previousItems) {
    itemsByKey.set(getStableRedlineItemKey(item), item);
  }

  for (const item of parsedItems) {
    itemsByKey.set(getStableRedlineItemKey(item), item);
  }

  return [...itemsByKey.values()];
}
